<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <!-- Background overlay -->
    <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal"></div>
    
    <!-- Modal container -->
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
      
      <!-- Modal panel -->
      <div class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
        <!-- Icon -->
        <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-4">
          <svg class="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
          </svg>
        </div>

        <!-- Content -->
        <div class="text-center">
          <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-2" id="modal-title">
            Delete CV
          </h3>
          
          <!-- CV Details -->
          <div v-if="cvData" class="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4 text-left">
            <h4 class="font-medium text-gray-900 dark:text-white mb-2">{{ cvData.title || 'Untitled CV' }}</h4>
            <div class="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <p v-if="cvData.target_role">
                <span class="font-medium">Target Role:</span> {{ cvData.target_role }}
              </p>
              <p v-if="cvData.target_company">
                <span class="font-medium">Target Company:</span> {{ cvData.target_company }}
              </p>
              <p>
                <span class="font-medium">Created:</span> {{ cvData.formatted_date }}
              </p>
              <p v-if="cvData.export_count > 0">
                <span class="font-medium">Downloads:</span> {{ cvData.export_count }}
              </p>
            </div>
          </div>

          <div class="text-sm text-gray-500 dark:text-gray-400 mb-6">
            <p class="mb-2">Are you sure you want to delete this CV?</p>
            <p class="font-medium text-red-600 dark:text-red-400">This action cannot be undone.</p>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
          <button 
            type="button" 
            @click="closeModal"
            :disabled="isDeleting"
            class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button 
            type="button"
            @click="handleDelete"
            :disabled="isDeleting"
            class="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            <span v-if="isDeleting" class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            <svg v-else class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
            <span>{{ isDeleting ? 'Deleting...' : 'Delete CV' }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'

export default {
  name: 'CvDeleteConfirmModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    cvData: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'confirm'],
  setup(props, { emit }) {
    const isDeleting = ref(false)

    const closeModal = () => {
      if (!isDeleting.value) {
        emit('close')
      }
    }

    const handleDelete = async () => {
      if (isDeleting.value) return
      
      isDeleting.value = true
      
      try {
        emit('confirm', props.cvData?.id)
      } catch (error) {
        console.error('Error in delete confirmation:', error)
      } finally {
        isDeleting.value = false
      }
    }

    return {
      isDeleting,
      closeModal,
      handleDelete
    }
  }
}
</script>

<style scoped>
/* Modal animations and styles handled by Tailwind classes */
</style>